import type { TableOrdering } from "@/components/BaseTable.vue"
import { useQuery } from "@tanstack/vue-query"
import type { MaybeRef } from "@vueuse/core"

export type ListUsersResponseItem = {
  id: string
  email: string
  firstName: string
  lastName: string
  role: string
  lastLogin: Date | null
  createdAt: Date | null
  isSuperuser: boolean
}

const parseUser = (
  user: RecursivelyConvertDatesToStrings<ListUsersResponseItem>
): ListUsersResponseItem => ({
  ...user,
  lastLogin: user.lastLogin
    ? DateFns.parseISO(user.lastLogin)
    : null,
  createdAt: user.createdAt
    ? DateFns.parseISO(user.createdAt)
    : null,
})

export type UserFilter = {
  search?: string
}

export type ListUsersOrdering =
  | "email"
  | "name"
  | "lastLogin"
  | "createdAt"

type UserQuery = UserFilter & {
  ordering?: TableOrdering<ListUsersOrdering>
} & Pagination

const listUsers = async (query: UserQuery) => {
  const { body } = await api.get<
    Paginate<
      RecursivelyConvertDatesToStrings<
        ListUsersResponseItem[]
      >
    >
  >("/users/", {
    query: query,
  })

  return {
    ...body,
    items: body.items.map((user) => parseUser(user)),
  }
}

export const useUsers = (
  query: MaybeRef<UserQuery>,
  enabled: MaybeRef<boolean> = true
) =>
  useQuery({
    queryKey: [USERS_QUERY_KEY, query] as const,
    enabled,
    queryFn: ({ queryKey: [_, query_] }) =>
      listUsers(unref(query_) as UserQuery),
    keepPreviousData: true,
  })
